import type { Breadcrumb, ErrorEvent, Event } from "@sentry/react";
// TransactionEvent isn't re-exported through @sentry/react; take it from
// @sentry/core (same version, the source @sentry/react itself re-exports from).
import type { TransactionEvent } from "@sentry/core";

// Ported from yuso-portals-backend/src/utils/sentryScrub.ts — the regexes below
// survived an adversarial PII gate; do not weaken them. Only the SDK types
// (@sentry/react instead of @sentry/node) and the browser-tracing paths
// (beforeSendTransaction, event.transaction on errors, event.spans[]) are
// frontend-specific.
//
// MIRROR-TO-BACKEND: this copy has intentionally diverged from the backend in
// two places that MUST be ported back to keep the two identical —
//   (1) the KBO regex now uses `[01]?` (was `0?`) to also catch 1-prefixed
//       enterprise numbers;
//   (2) the shared request/user/extra/contexts block is extracted into
//       scrubCommonFields so the error and transaction paths can't drift.
// A separate backend PR applies both.
//
// Inherent limit: customer NAMES (person/company) aren't regex-detectable, so
// they are NOT scrubbed here. The "PII-safe" claim covers structured
// identifiers (JWT/IBAN/email/EAN/KBO) only. Keep sendDefaultPii=false and the
// replay masking config as the primary defence for free-text PII.
//
// Redaction order matters: JWT first (its base64 segments can hold long digit
// runs the EAN pattern would otherwise eat); IBAN and email before the bare
// digit-run patterns; EAN before KBO (EAN is the longer run, so it claims a
// long sequence before KBO can bite a 9-10 digit slice out of it). The digit
// patterns tolerate spaces/dots between groups because this codebase's own PII
// arrives grouped: spaced IBANs, dotted KBO/VAT, `EAN…`-prefixed and spaced
// EANs (mandates.ean is stored `EAN`-prefixed). Over-redaction is preferred to
// leaking.
export function scrubString(input: string): string {
  if (typeof input !== "string") return input;
  return input
    .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]+/g, "[JWT]")
    // IBAN, two shapes: compact (bounded so it never swallows following words)
    // and space-grouped (print format: groups of exactly 4, optional short
    // trailing DIGIT group). Exact-4 + digits-only tail stop it from eating
    // adjacent words (e.g. "…7034 for alex@…" must not swallow "for alex").
    .replace(/\b[A-Z]{2}\d{2}[A-Za-z0-9]{10,30}\b/g, "[IBAN]")
    .replace(/\b[A-Z]{2}\d{2}(?: [A-Za-z0-9]{4}){2,6}(?: \d{1,3})?\b/g, "[IBAN]")
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[EMAIL]")
    .replace(/\bEAN[ .]?\d(?:[ .]?\d){9,}/g, "[EAN]")
    .replace(/\b\d(?:[ .]?\d){12,17}\b/g, "[EAN]")
    // Leading digit is `[01]?`: Belgian enterprise numbers are `0`- or (newer)
    // `1`-prefixed 10-digit; the optional group also lets the 9-digit VAT body
    // through.
    .replace(/\b(?:BE[ ]?)?[01]?\d{3}[ .]?\d{3}[ .]?\d{3}\b/g, "[KBO]");
}

// Recursively apply scrubString to every string in an object/array graph.
// Bounded depth + cycle guard so a malformed/self-referential payload can't
// spin. Used for the loosely-typed bags (breadcrumb.data, event.extra,
// event.contexts) that structured field-scrubbing can't reach.
//
// MUST NOT MUTATE ITS INPUT. A breadcrumb's data bag can hold live references
// to application state — a console breadcrumb puts the very objects passed to
// console.log into data.arguments — so writing back into the graph rewrites the
// running app's own data. That is not hypothetical: an in-place version of this
// function replaced the KBO and email on the add-company form with the literals
// "[KBO]" and "[EMAIL]" (the user picked a company, console.log handed Sentry a
// reference, and the scrub edited the object React was rendering from).
// Returning fresh containers keeps the redaction confined to the Sentry payload.
const MAX_DEPTH = 6;
export function deepScrub(
  value: unknown,
  depth = 0,
  seen: WeakMap<object, unknown> = new WeakMap()
): unknown {
  if (typeof value === "string") return scrubString(value);
  if (value === null || typeof value !== "object" || depth >= MAX_DEPTH) {
    return value;
  }
  // Cycle guard returns the copy already made for this node, so a shared or
  // self-referential subtree stays shared in the output instead of recursing.
  const existing = seen.get(value);
  if (existing !== undefined) return existing;

  if (Array.isArray(value)) {
    const copy: unknown[] = [];
    seen.set(value, copy);
    for (let i = 0; i < value.length; i++) {
      copy[i] = deepScrub(value[i], depth + 1, seen);
    }
    return copy;
  }
  // Opaque built-ins keep their data in internal slots, so Object.keys() is
  // empty and rebuilding from it would destroy them. Pass them through: they
  // hold no scrubbable strings.
  if (
    value instanceof Date ||
    value instanceof RegExp ||
    value instanceof Map ||
    value instanceof Set ||
    value instanceof WeakMap ||
    value instanceof WeakSet ||
    value instanceof Promise ||
    value instanceof ArrayBuffer ||
    ArrayBuffer.isView(value)
  ) {
    return value;
  }

  // Everything else — plain objects AND class instances — gets copied and
  // scrubbed. Class instances are NOT exempt: an Error subclass, an axios
  // error or an AxiosHeaders instance carries its data in ordinary own
  // enumerable properties, and those routinely hold PII (customer emails on a
  // mandate error, a live Bearer JWT on `config.headers.Authorization`).
  // Skipping them leaked exactly that. Preserve the prototype so `instanceof`
  // still holds for Sentry's serialiser, and carry non-enumerable own
  // properties across so an Error keeps its message and stack.
  const proto = Object.getPrototypeOf(value) as object | null;
  const copy: Record<string, unknown> =
    proto === Object.prototype || proto === null
      ? {}
      : (Object.create(proto) as Record<string, unknown>);
  seen.set(value, copy);

  for (const key of Object.getOwnPropertyNames(value)) {
    const desc = Object.getOwnPropertyDescriptor(value, key);
    if (!desc) continue;
    // Accessors must be READ and scrubbed, not cloned. Copying the descriptor
    // verbatim looks conservative but is the opposite: Sentry's normalize()
    // walks own enumerable properties and reads them, so it invokes the getter
    // two frames later — past this scrubber — and ships the raw value. An
    // earlier revision of this file cloned the descriptor "to avoid getter side
    // effects"; the side effect happens either way, the only difference is
    // whether the value gets redacted first.
    //
    // Invoke on `value` (the original), never on `copy`: the original owns the
    // state the getter reads, so this also avoids an Illegal-invocation throw
    // on host objects and classes with private fields. A getter that throws
    // drops its key rather than taking the whole payload down with it.
    if (!("value" in desc)) {
      if (typeof desc.get !== "function") continue; // setter-only: nothing to read
      // Only ENUMERABLE accessors can leak, so only those are worth invoking:
      // normalize() walks own enumerable properties, so a non-enumerable getter
      // is never read and has nothing to scrub. Invoking one anyway would be a
      // gratuitous side effect on an object we do not own — a lazy field that
      // caches itself by redefining the property on `this` would write to the
      // caller's object, which is the no-mutation invariant this file exists to
      // protect. So a non-enumerable accessor is re-exposed, never invoked here.
      //
      // Re-exposing it is NOT the same as cloning the descriptor verbatim, which
      // is what an earlier revision did. A verbatim clone is wrong for an engine-native
      // getter backed by an internal slot: its getter runs with `this`
      // bound to `copy`, an object the engine never installed that slot on, so
      // it yields undefined. V8's lazy `Error.stack` is exactly that, and it is
      // an own ACCESSOR on Node 22+ / current Chrome (it was a plain data
      // property on Node 20, which is why this surfaced only on the runtime
      // bump). The effect was silent and not test-shaped: every scrubbed Error
      // reached Sentry with no stack at all, which is the one field its
      // serialiser needs most. Delegate the read to the original object —
      // still lazy, still no side effect at clone time — and scrub what comes
      // back, since a stack's first line is the message and can carry PII.
      if (!desc.enumerable) {
        const get = desc.get;
        Object.defineProperty(copy, key, {
          configurable: true,
          enumerable: false,
          ...(desc.set ? { set: desc.set } : {}),
          get: () => {
            try {
              return deepScrub(get.call(value), depth + 1, new WeakMap());
            } catch {
              return undefined;
            }
          },
        });
        continue;
      }
      let got: unknown;
      try {
        got = desc.get.call(value);
      } catch {
        continue;
      }
      Object.defineProperty(copy, key, {
        enumerable: true,
        configurable: true,
        writable: true,
        value: deepScrub(got, depth + 1, seen),
      });
      continue;
    }
    Object.defineProperty(copy, key, {
      ...desc,
      writable: true,
      value: deepScrub(desc.value, depth + 1, seen),
    });
  }
  return copy;
}

// Shared request-data scrub for both error and transaction events. Browser
// events usually have no `request` at all, so every field is null-guarded.
function scrubRequest(request: NonNullable<ErrorEvent["request"]>): void {
  if (typeof request.url === "string") {
    request.url = scrubString(request.url);
  }
  if (typeof request.query_string === "string") {
    request.query_string = scrubString(request.query_string);
  }
  if (request.headers) {
    delete request.headers.authorization;
    delete request.headers.Authorization;
    delete request.headers.cookie;
    delete request.headers.Cookie;
    delete request.headers["set-cookie"];
    delete request.headers["Set-Cookie"];
  }
  delete request.cookies;
  // Belt-and-braces: the request body is default-denied today, but a
  // regression could re-attach it — sign/onboarding bodies carry IBANs +
  // signatory emails. Never let it through.
  delete request.data;
}

// request/user/extra/contexts are shared by error and transaction events; keep
// their scrub in one place so the two paths can't drift. Manual capture can
// attach arbitrary PII via extra/contexts, which the structured field scrubs
// don't reach — hence the deepScrub.
function scrubCommonFields(event: Event): void {
  if (event.request) {
    scrubRequest(event.request);
  }
  delete event.user;
  // Assign the returned copy: deepScrub no longer writes back into its input,
  // and these bags can reference live app objects.
  if (event.extra) event.extra = deepScrub(event.extra) as Event["extra"];
  if (event.contexts)
    event.contexts = deepScrub(event.contexts) as Event["contexts"];
}

export function scrubEvent(event: ErrorEvent): ErrorEvent {
  // event.transaction (active route name) rides on ERROR events too and can
  // hold a raw route path (e.g. /sign/<token>) before React Router
  // parameterises it.
  if (typeof event.transaction === "string") {
    event.transaction = scrubString(event.transaction);
  }

  scrubCommonFields(event);

  if (event.exception?.values) {
    for (const value of event.exception.values) {
      if (typeof value.value === "string") {
        value.value = scrubString(value.value);
      }
    }
  }

  if (typeof event.message === "string") {
    event.message = scrubString(event.message);
  }

  return event;
}

// Transactions can carry PII in the transaction name (an unparameterised route
// path), in request.url/query_string, and in child spans. No exception/message
// on a transaction event.
export function scrubTransaction(event: TransactionEvent): TransactionEvent {
  if (typeof event.transaction === "string") {
    event.transaction = scrubString(event.transaction);
  }

  scrubCommonFields(event);

  // Browser tracing attaches http.client spans whose description is
  // "<METHOD> <path>" and whose data holds the full request URL/query — both
  // can carry raw EANs/companyId/ids before the path is parameterised.
  if (event.spans) {
    for (const span of event.spans) {
      if (typeof span.description === "string") {
        span.description = scrubString(span.description);
      }
      if (span.data) span.data = deepScrub(span.data) as typeof span.data;
    }
  }

  return event;
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = scrubString(breadcrumb.message);
  }
  // Deep-scrub the whole data bag: HTTP breadcrumbs (fetch/xhr) put the query
  // string in data["http.query"]/["http.fragment"], not just .url. Console
  // breadcrumbs put the logged objects themselves in data.arguments, so this
  // MUST take deepScrub's copy rather than edit the bag in place.
  if (breadcrumb.data)
    breadcrumb.data = deepScrub(breadcrumb.data) as Breadcrumb["data"];
  return breadcrumb;
}
